import { isEscapeKey } from '../helpers/escape-key-detection';
import { lockBody, unlockBody } from '../helpers/lockBody';
import { debounce } from 'lodash';


// Static strings
const openMenuClass = 'nav-menu-is-open';
const navLoadedClass = 'nav-has-loaded';
const menuSlideStateAttribute = 'data-nav-expanded';
const navId = "navContainer";
const menuId = "nav-mobile-menu";

class MainNavigation {
    container: HTMLElement;
    menuContainer: HTMLElement;
    menuTrigger: HTMLButtonElement;
    menuPanel: HTMLElement;
    menuFocusableChildren: Array<HTMLButtonElement | HTMLAnchorElement | HTMLInputElement>;
    menuIsOpen: boolean;
    navIsShowing: boolean;
    existingBodyStyles: string;
    lastScrollPosition: number;
    hasLoaded: boolean;
    currentNavHeight: number;
    bodyEl: HTMLBodyElement;
    onPageAnchorLinks: HTMLAnchorElement[];
    navSearchCloseBtn: null | HTMLElement;
    navButtonItems: HTMLButtonElement[];
    currentlyOpenItem: HTMLElement;
    navMain: HTMLElement;
    htmlElement: HTMLElement;
    navUtility: HTMLElement;
    constructor(navigation: HTMLElement) {
        this.htmlElement = document.documentElement;
        this.container = navigation;
        this.menuTrigger = this.container.querySelector(`[aria-controls="${menuId}"]`) as HTMLButtonElement;
        this.menuPanel = document.querySelector(`#${menuId}`) as HTMLElement;
        this.menuFocusableChildren = [].slice.call(this.menuPanel.querySelectorAll('button, a, input'));
        this.bodyEl = document.body as HTMLBodyElement;
        this.menuIsOpen = false;
        this.navIsShowing = true;
        this.hasLoaded = false;
        this.existingBodyStyles = '';
        this.navSearchCloseBtn = null;
        this.navButtonItems = Array.from(this.container.querySelectorAll(`button[aria-controls]:not([aria-controls=${menuId}])`));
        this.navMain = this.container.querySelector('[data-nav-main]');
        this.navUtility = this.container.querySelector('#navUtility')
    }

    init = () => {
        document.body.addEventListener('click', (e: MouseEvent) => {
            this.checkClickAndCloseOpenItems(e);
        });

        this.menuTrigger.addEventListener('click', () => {
            if (this.menuIsOpen) {
                this.closeMenu();
            }
            else {
                this.openMenu();
            }
        });

        if (this.navButtonItems.length > 0) {
            this.navButtonItems.forEach((button) => {
                button.addEventListener('click', (e: MouseEvent) => {
                    this.toggleInert(e.target as HTMLButtonElement)
                });
            })
        }

        document.body.addEventListener('keyup', (e: KeyboardEvent) => {
            if (e.which == 27 && this.menuIsOpen) { // Escape key
                this.closeMenu();
                this.menuTrigger.focus();
            }
            if (e.which == 27 && this.currentlyOpenItem) { // Escape key
                this.closeOpenItems();
            }
            if (e.which == 9) { // Tab key. Show nav if tabbed into, or close menu if tabbed out of.
                const focusInNav = this.container.contains(document.activeElement);
                if (focusInNav && !this.navIsShowing) {
                    this.slideDown();
                }
                if (!focusInNav && this.menuIsOpen) {
                    this.closeMenu();
                }
                if (!focusInNav && this.currentlyOpenItem) {
                    this.closeOpenItems();
                }
            }
        });
        // Setup on page load
        this.closeMenu();
        this.setPageOffset();
        this.slideDown();
        
    }

    setExpandedTrue = (el: HTMLElement) => {
        el.setAttribute('aria-expanded', 'true')
    }

    setExpandedFalse = (el: HTMLElement) => {
        el.setAttribute('aria-expanded', 'false')
    }

    addInert = (el: HTMLElement) => {
        el.setAttribute('inert', '');
    }

    removeInert = (el: HTMLElement) => {
        el.removeAttribute('inert');
    }

    toggleInert = (button: HTMLButtonElement) => {
        var elementToToggle = document.querySelector(`#${button.getAttribute('aria-controls')}`) as HTMLElement;
        //close any open items
        this.closeOpenItems(elementToToggle);

        if (elementToToggle) {
            if (elementToToggle.hasAttribute('inert')) {
                this.removeInert(elementToToggle);
                this.setExpandedTrue(button);
                this.currentlyOpenItem = elementToToggle;
            }
            else {
                this.setExpandedFalse(button);
                this.addInert(elementToToggle);
                this.currentlyOpenItem = null;
            }
        }
    }

    closeOpenItems = (elementToToggle?: HTMLElement) => { 
        this.navButtonItems.forEach((btn) => {
            const elementToClose = document.querySelector(`#${btn.getAttribute('aria-controls')}`) as HTMLElement;
            if (elementToClose !== elementToToggle) {
                this.addInert(elementToClose); 
                this.setExpandedFalse(btn);
            }
        });
    }

    slideDown = () => {
        this.navIsShowing = true;
        this.htmlElement.setAttribute(menuSlideStateAttribute, 'true');
        this.setCssVariables();
    }

    slideUp = () => {
        this.navIsShowing = false;
        this.htmlElement.setAttribute(menuSlideStateAttribute, 'false');
        this.setCssVariables();
    }

    openMenu = () => {
        this.menuPanel.setAttribute('aria-hidden', 'false');
        this.menuTrigger.setAttribute('aria-expanded', 'true');
        this.enableTabbableChildren();
        this.menuIsOpen = true;
        this.existingBodyStyles = lockBody();
        this.container.classList.add(openMenuClass);
    }

    closeMenu = () => {
        this.menuPanel.setAttribute('aria-hidden', 'true');
        this.menuTrigger.setAttribute('aria-expanded', 'false');
        this.disableTabbableChildren();
        this.menuIsOpen = false;
        unlockBody(this.existingBodyStyles);
            this.container.classList.remove(openMenuClass);
        this.setPageOffset();
    }

    enableTabbableChildren = () => {
        this.menuFocusableChildren.forEach(child => {
            child.tabIndex = 0;
        })
    }

    disableTabbableChildren = () => {
        this.menuFocusableChildren.forEach(child => {
            child.tabIndex = -1;
        })
    }

    setPageOffset = () => {
        if (!this.hasLoaded) {
            this.container.classList.add(navLoadedClass);
            this.hasLoaded = true;
        }
        this.updateNavHeight();
    }

    updateNavHeight = () => {
        this.currentNavHeight = this.container.offsetHeight;
        this.setCssVariables();
    }

    scrollHandler = () => {
        if (this.hasLoaded) {
            const currentPosition = window.pageYOffset;
            const delta = currentPosition - this.lastScrollPosition;
            this.lastScrollPosition = currentPosition;
            if (this.navIsShowing) {
                if (delta > 0 && currentPosition > this.currentNavHeight && !this.menuIsOpen) {
                    this.slideUp();
                    this.closeOpenItems();
                }
            }
            else if (delta < 0 || currentPosition < this.currentNavHeight) {
                this.slideDown();
            }
        }
    }

    resizeHandler = () => {
        this.setPageOffset();
    }

    setCssVariables = () => {
        if (this.currentNavHeight === undefined)
            this.currentNavHeight = this.container.offsetHeight;
        document.documentElement.style.setProperty('--nav-clearance', `${this.navIsShowing ? this.currentNavHeight : this.currentNavHeight}px`);
        document.documentElement.style.setProperty('--nav-margin-offset', `${this.navIsShowing ? this.currentNavHeight : 0}px`);
        document.documentElement.style.setProperty('--nav-utility-clearance', `${document.querySelector('[data-alert-global]') ? this.navUtility.offsetHeight + (document.querySelector('[data-alert-global]') as HTMLElement).offsetHeight : this.navUtility.offsetHeight}px`);
    }

    get isTheMenuOpen() {
        return this.menuIsOpen;
    }

    checkClickAndCloseOpenItems = (e: MouseEvent) => {
        if (this.currentlyOpenItem !== null && this.currentlyOpenItem !== undefined) {
            const parentTrigger = document.querySelector(`[aria-controls=${this.currentlyOpenItem.getAttribute('id')}]`);
            const parent = this.currentlyOpenItem as Node;
            const clickedElement = e.target as Node;

            if (parent.contains(clickedElement) || parentTrigger === e.target) {
            } else {
                this.closeOpenItems();
            }
        }

    }

    isMobileWidth = () => {
        return window.innerWidth < 1280;
    }
}

let mainNavigation: MainNavigation = null;
const navContainer = document.querySelector(`#${navId}`) as HTMLElement;
if (navContainer) {
    mainNavigation = new MainNavigation(navContainer);
    (window as any).mainNavigation = mainNavigation;
}
 
export { mainNavigation };




